import React, { useEffect, useRef, useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { FormProvider, useForm } from "react-hook-form";
import { useSearchParams } from "react-router";

import ButtonLoading from "~/atoms/buttons/ButtonLoading";
import Link from "~/atoms/Link";
import Alert from "~/atoms/notifications/Alert";
import SkeletonLoader from "~/atoms/SkeletonLoader";
import Checkbox from "~/components/form/Checkbox";
import Input from "~/components/form/Input";
import PasswordRequirements from "~/components/form/PasswordRequirements";
import Recaptcha, { type RecaptchaHandle } from "~/components/form/Recaptcha";
import useAuthFlowCodes from "~/hooks/useAuthFlowCodes";
import { setLocalStorageItem } from "~/lib/localStorage";
import { createUser } from "~/lib/payload/users";
import { useSession } from "~/providers/SessionProvider";
import type { UserRole } from "~/types";
import { emailRegexPattern, passwordRegexPattern } from "~/util/regex";
import { trackSignUp } from "~/util/tracking";

interface FormData {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  terms: boolean;
  recaptcha: string;
}

interface FormProps {
  loginUrl?: string;
  loginOnClick?: () => void;
  full?: boolean;
  type: UserRole;
  onSuccess?: () => void;
}

const Form: React.FC<FormProps> = ({
  loginUrl,
  loginOnClick,
  full,
  type,
  onSuccess,
}) => {
  const [searchParams] = useSearchParams();
  const initialFocusRef = useRef<HTMLInputElement>(null);
  const recaptchaRef = useRef<RecaptchaHandle>(null);
  const token = searchParams.get("token");

  const { signIn } = useSession();
  const {
    isLoading: isLoadingPresetEmail,
    email: presetEmail,
    firstName: presetFirstName,
    lastName: presetLastName,
  } = useAuthFlowCodes();

  const methods: UseFormReturn<FormData> = useForm<FormData>();
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!isLoadingPresetEmail) {
      if (presetFirstName) methods.setValue("firstName", presetFirstName);
      if (presetLastName) methods.setValue("lastName", presetLastName);
      if (presetEmail) methods.setValue("email", presetEmail);
    }
  }, [
    isLoadingPresetEmail,
    presetEmail,
    presetFirstName,
    presetLastName,
    methods,
  ]);

  const onSubmit = async (data: FormData) => {
    setGeneralError(null);
    setLoading(true);

    const { email, firstName, lastName, password, recaptcha } = data;

    const createRes = await createUser({
      email,
      password,
      firstName,
      lastName,
      recaptchaToken: recaptcha, // Recaptcha.
      emailToken: token, // Email verification token.
      landlord: type === "landlord",
    });

    if (createRes?.success) {
      setLocalStorageItem("role", type);

      // Track the sign up event
      trackSignUp(createRes?.data?.doc?.id, type);

      // Login the user.
      const signinRes = await signIn(email, password, type);

      if (signinRes?.success) {
        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        // Redirect is handled on parent layout.
        return;
      } else {
        setGeneralError(signinRes?.message || "Failed to sign in");
        setLoading(false);
      }
    } else {
      if ("field" in createRes) {
        methods.setError(createRes.field as keyof FormData, {
          type: "custom",
          message: createRes.message,
        });
      } else {
        setGeneralError(createRes?.message || "Failed to create account");
      }
      // Reset recaptcha so user can try again without waiting for timeout
      recaptchaRef.current?.reset();
      setLoading(false);
    }
  };

  return (
    <>
      <h2 className="[@media(max-height:830px)] mb-4 text-2xl leading-9 font-bold tracking-tight text-gray-900 [@media(max-height:830px)]:mb-2 [@media(max-height:830px)]:leading-none">
        Create your Free Account
      </h2>

      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          className="space-y-6 [@media(max-height:930px)]:space-y-2"
        >
          {isLoadingPresetEmail && (
            <div>
              <SkeletonLoader width={43} height={24} />
              <SkeletonLoader width="100%" height={36} className="mt-2" />
              <SkeletonLoader width="100%" height={36} className="mt-2" />
            </div>
          )}

          {!isLoadingPresetEmail && (
            <div className="mt-4 flex flex-wrap gap-4 sm:flex-nowrap [@media(max-height:830px)]:mt-2">
              <div className="flex-auto">
                <Input
                  label="First Name"
                  id="firstName"
                  name="firstName"
                  required
                  ref={initialFocusRef}
                  inputProps={{
                    autoComplete: "given-name",
                  }}
                />
              </div>
              <div className="flex-auto">
                <Input
                  label="Last Name"
                  id="lastName"
                  name="lastName"
                  inputProps={{
                    autoComplete: "family-name",
                  }}
                  required
                />
              </div>
            </div>
          )}

          {!isLoadingPresetEmail && (
            <Input
              id="email"
              name="email"
              label="Email"
              type="email"
              required="Email is required."
              disabled={!!presetEmail}
              inputProps={{
                autoComplete: "email",
              }}
              rules={{
                pattern: {
                  value: emailRegexPattern,
                  message: "Please enter a valid email address",
                },
              }}
            />
          )}

          <Input
            id="password"
            name="password"
            label="Password"
            type="password"
            required="Password is required."
            inputProps={{
              // @ts-expect-error -- passwordrules is a custom attribute for password generators like 1Password.
              passwordrules:
                "minlength: 8; required: lower; required: upper; required: digit; required: [!#$%&'()*+,./;<>?@^_{}~];",
              autoComplete: "new-password",
            }}
            rules={{
              validate: {
                minLength: (value: string) =>
                  value.length > 7 || "Needs a minimum of 8 characters.",
                hasLowercase: (value: string) =>
                  /[a-z]/.test(value) || "Need a lowercase letter",
                hasUppercase: (value: string) =>
                  /[A-Z]/.test(value) || "Need a minimum of 1 capital letter",
                hasNumber: (value: string) =>
                  /[0-9]/.test(value) || "Need a minimum of 1 number",
                hasSpecialCharacter: (value: string) =>
                  passwordRegexPattern.test(value) ||
                  "Need a special character",
              },
            }}
          />

          <PasswordRequirements />

          <Checkbox
            id="terms"
            name="terms"
            label="I agree to the <a class='font-semibold text-blue-600' target='_blank' href='https://rocketrent.com/policies/terms-and-conditions'>terms and conditions</a>"
            required="You must agree to the terms and conditions."
          />
          <Recaptcha ref={recaptchaRef} />

          {generalError && <Alert type="danger">{generalError}</Alert>}

          <div>
            <ButtonLoading loading={loading} type="submit" fullWidth={full}>
              Create account
            </ButtonLoading>
          </div>
        </form>
      </FormProvider>
      <p className="mt-5 text-sm text-gray-500">
        Already have an account?{" "}
        {loginUrl && (
          <Link
            href={loginUrl}
            className="leading-6 font-semibold text-blue-600 hover:text-blue-500"
          >
            Login here
          </Link>
        )}
        {loginOnClick && (
          <button
            type="button"
            onClick={loginOnClick}
            className="leading-6 font-semibold text-blue-600 hover:text-blue-500"
          >
            Login here
          </button>
        )}
      </p>
    </>
  );
};

export default Form;
